import { Component, Renderer2, ElementRef, ViewChild, signal, inject, PendingTasks, Inject, DOCUMENT, NO_ERRORS_SCHEMA } from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "../../environments/environment";
import { AnalyticsService } from "../service/analytics.service";
import { GeneralService } from "../service/general.service";
import { AbstractComponent } from "./abstract/abstract.component";
import { Pair } from "../obj/Pair";
import { RateClass } from "../entity/RateClass";
import { Utility } from "../entity/Utility";
import { LegendPosition } from "@swimlane/ngx-charts";
import { RateData } from "../obj/RateData";
import { FAQ } from "../entity/FAQ";
import { LoaderService } from "../service/loader.service";
import { AccountNumberIssueDialog } from "./incs/account-number-issue-dialog.component";
import { HeaderComponent } from "./incs/header.component";
import { FormsModule } from "@angular/forms";
import { lastValueFrom } from "rxjs";
import { AuthService } from "../service/auth.service";

@Component({
    selector: "kilowant-home",
    templateUrl: "../../html/main-home.html",
    schemas: [
        NO_ERRORS_SCHEMA
    ],
    imports: [HeaderComponent, RouterLink, FormsModule]
})
export class HomeComponent extends AbstractComponent {
    showVideo = signal(false);
    @ViewChild("video") videoElement: ElementRef;
    @ViewChild("#leftSeal") leftSealElement: ElementRef;

    utilities: Utility[];
    currentUtility: Utility;
    currentRateClass: RateClass;
    rateClasses: RateClass[];
    topSavers: object[];

    viewTopOffers: [number, number] = [400, 200];
    legend: boolean = true;
    legendTitle: string = "";
    legendPosition: LegendPosition = LegendPosition.Below;
    offersForChart: RateData[];
    savings: number[];
    colorScheme = { domain: ["#D0D0D0", "#A0A0A0"], name: "kilowant", selectable: true, group: null! };

    imageMode = signal(-1);
    billsToUpload: File[] = [];
    multiMode: boolean = false;
    step: number = 1;

    faq = signal<FAQ[]>([]);
    faqOpenQuestion = signal(-1);

    registrationProcess = signal(false);
    loading = signal(false);
    progress = signal(false);
    progressWidth = signal(1);
    joinEmail = signal("");
    initData = inject(PendingTasks);
    constructor(
        router: Router,
        route: ActivatedRoute,
        dialog: MatDialog,
        analyticsService: AnalyticsService,
        protected loaderService: LoaderService,
        generalService: GeneralService,
        private authService: AuthService,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document
    ) {
        super(router, route, dialog, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.loaderService.setDisabled(true);
        this.renderer["addClass"](this.document.body, "no-back");
        super.ngOnInit();
    }

    pageStart(): void {
        if (this.router.url != "/" && this.router.url != "") {
            let affiliate = this.router.url;
            if (affiliate.startsWith("/")) {
                affiliate = affiliate.substring(1);
            }
            if (affiliate.indexOf("/") != -1) {
                affiliate = affiliate.substring(0, affiliate.indexOf("/") - 1);
            }
            this.generalService.saveAffiliate(affiliate);
            this.router.navigate(["/"], { replaceUrl: true });
        }
        let faq$ = this.generalService.getFAQ(4);
        this.initData.run(async () => {
            this.faq.set(await lastValueFrom(faq$));
        });
    }

    ngAfterViewInit(): void {
        let script = this.renderer.createElement("script");
        script.type = `text/javascript`;
        script.text = `
                (function () {
                 var bbb = document.createElement("script");
                 bbb.type = "text/javascript";
                 bbb.async = true;
                 bbb.src = "https://seal-boston.bbb.org/v3/seals/blue-seal-120-61-bbb-570609.js";
                 var s = document.getElementsByTagName("script")[0];
                 s.parentNode.insertBefore(bbb, s);
                })();
             `;
        this.renderer.appendChild(this.document.body, script);
    }

    start(): void {
        if (!this.authService.isLoggedIn()) {
            this.registrationProcess.set(true);
        } else if (this.authService.isAdminLoggedIn()) {
            this.goto("/admin-kilowant-area");
        } else {
            this.goto("/user/home");
        }
    }

    getRateClassAbbr(rateClass: string | null): string {
        if (rateClass == null) {
            return "";
        }
        return this.generalService.getRateClassAbbr(rateClass);
    }

    startVideo(): void {
        this.showVideo.set(true);
        this.videoElement.nativeElement.play();
        setTimeout(() => { this.generalService.scrollPageTop(0) }, 10);
    }

    returnedFromHeader(): void {
        if (this.showVideo()) {
            this.hideVideo();
        } else {
            this.showLogin();
        }
    }

    hideVideo(): void {
        this.videoElement.nativeElement.pause();
        this.videoElement.nativeElement.currentTime = 0;
        this.document.exitFullscreen();
        this.showVideo.set(false);
    }

    switchMode(newMode: number): void {
        this.imageMode.set(newMode);
        this.clearErrors();
    }

    getFirstFAQ(): string {
        return "<h2>" + this.faq()[0].question + "</h2><p>" + this.faq()[0].answer + "</p>";
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    resetError(): void {
        this.clearErrors();
    }

    showLogin(): void {
        this.trackButtonClick("sign_in");
        this.router.navigate(["user/login"]);
    }

    goToRegister(): void {
        this.trackButtonClick("sign_up_cta");
        this.router.navigate(["/user/registration"]);
    }

    parseBill(): void {
        this.loaderService.setDisabled(true);
        this.loading.set(true);
        this.progress.set(true);
        var t = this;
        this.progressWidth.set(0);
        this.clearErrors();
        setTimeout(() => { t.progressWidth.set(100); t.progress.set(false); }, environment.BILL_PARSE_LENGTH);

        this.generalService.parseBillAndRegister(this.billsToUpload).subscribe({
            next: (response: Pair) => {
                this.loading.set(false);
                this.loaderService.setDisabled(false);
                this.goto("/account/init");
            },
            error: (err: any) => {
                this.loading.set(false);
                this.loaderService.setDisabled(false);
                this.setErrorText(err.error);
                this.billsToUpload = [];
                return;
            }
        });
    }

    contact(isGeneral: boolean): void {
        if (isGeneral) {
            location.href = 'mailto:info@kilowant.com?subject=Kilowant Contact';
        } else {
            location.href = 'mailto:jgrasso@kilowant.com?subject=Kilowant Contact';
        }
    }

    trackButtonClick(info: string): void {
        this.analyticsService.trackEvent("button_click", { label: info });
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.billsToUpload.push((target.files as FileList).item(0) as File);
        this.parseBill();
        return;
    }

    deleteBill(pos: number) {
        this.billsToUpload.splice(pos, 1);
    }

    downloadBill(pos: number) {
        this.generalService.sendFileToClient(this.billsToUpload[pos]);
    }

    viewQuestion(i: number): void {
        if (this.faqOpenQuestion() == i) {
            this.faqOpenQuestion.set(-1);
        } else {
            this.faqOpenQuestion.set(i);
        }
    }

    reportAccountIssue(accountNumber: string): void {
        var dialogRef = this.dialog.open(AccountNumberIssueDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        dialogRef.componentInstance.info = "Please, provide more details about your account (optional).";
        dialogRef.componentInstance.message = "My Account Number: " + accountNumber + " has been used without my authorization. Please, investigate this.";
        dialogRef.componentInstance.utility = null;
        dialogRef.componentInstance.accountNumber = accountNumber;

        dialogRef.afterClosed().subscribe((result: any) => {
            dialogRef = null!;
        });
    }

    joinWeblistSubmit(): void {
        this.clearErrors();
        if (this.joinEmail() == null || this.joinEmail().length == 0) {
            this.addError("joinEmail");
        }

        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to join our waitlist.");
            this.analyticsService.trackEvent("form_error", { form: "waitlist", error_key: "missing_fields" });
            return;
        }

        this.generalService.send({
            state: '', name: '', email: this.joinEmail()
        }).subscribe({
            next: (response: Pair) => {
                this.openResultsPopup("Thank you for joining", "We will inform you as soon as we extend our services.", null);
            },
            error: (err: any) => {
                if (err.status == 400) {
                    this.addError("email");
                    this.setErrorText(err.error);
                    this.analyticsService.trackEvent("form_error", { form: "waitlist", error_key: "send_rejected" });
                }
            }
        }
        );
    }
}